import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Bars3Icon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import WarningBanner from '@/components/WarningBanner'
import type { UnverifiedFieldMatch } from './monthlyPerformanceUtils'

export type CommentaryTextItem = { text: string }

function newRowKey(): string {
  return crypto.randomUUID()
}

function useRowKeys(
  itemCount: number,
  resetKey: string,
): [string[], Dispatch<SetStateAction<string[]>>] {
  const [keys, setKeys] = useState<string[]>([])
  const resetKeyRef = useRef(resetKey)

  useEffect(() => {
    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey
      setKeys(Array.from({ length: itemCount }, () => newRowKey()))
      return
    }
    setKeys((prev) => {
      if (prev.length === itemCount) return prev
      if (prev.length < itemCount) {
        const added = Array.from({ length: itemCount - prev.length }, () => newRowKey())
        return [...prev, ...added]
      }
      return prev.slice(0, itemCount)
    })
  }, [itemCount, resetKey])

  return [keys, setKeys]
}

function SortableCommentaryRow({
  rowKey,
  text,
  readOnly,
  unverified,
  unverifiedReason,
  onTextChange,
  onRemove,
  onInsertBelow,
  insertDisabled,
}: {
  rowKey: string
  text: string
  readOnly: boolean
  unverified: boolean
  unverifiedReason?: string
  onTextChange: (text: string) => void
  onRemove: () => void
  onInsertBelow: () => void
  insertDisabled: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rowKey,
    disabled: readOnly,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-2 ${isDragging ? 'relative z-10 rounded bg-white shadow-md' : ''}`}
    >
      {!readOnly ? (
        <Button
          type="button"
          variant="ghost"
          iconOnly
          className="mt-1 shrink-0 cursor-grab active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <Bars3Icon className="h-4 w-4" aria-hidden />
        </Button>
      ) : null}
      <textarea
        className={`min-w-0 flex-1 rounded border px-3 py-2 text-sm ${
          unverified ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
        }`}
        rows={2}
        readOnly={readOnly}
        value={text}
        title={unverifiedReason}
        onChange={(e) => onTextChange(e.target.value)}
      />
      {!readOnly ? (
        <div className="mt-1 flex shrink-0 items-start gap-1">
          <Button
            type="button"
            variant="outline"
            iconOnly
            size="sm"
            aria-label="Remove row"
            onClick={onRemove}
          >
            <MinusIcon className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="outline"
            iconOnly
            size="sm"
            disabled={insertDisabled}
            aria-label="Insert row below"
            onClick={onInsertBelow}
          >
            <PlusIcon className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ) : null}
    </div>
  )
}

type CommentaryTextListProps = {
  items: CommentaryTextItem[]
  pathPrefix: string
  readOnly: boolean
  resetKey: string
  unverifiedPaths: Set<string>
  unverifiedMatches: UnverifiedFieldMatch[]
  maxItems?: number
  addButtonLabel?: string
  generationError?: string | null
  onChange: (items: CommentaryTextItem[]) => void
}

export default function CommentaryTextList({
  items,
  pathPrefix,
  readOnly,
  resetKey,
  unverifiedPaths,
  unverifiedMatches,
  maxItems,
  addButtonLabel,
  generationError,
  onChange,
}: CommentaryTextListProps) {
  const [rowKeys, setRowKeys] = useRowKeys(items.length, resetKey)

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = rowKeys.indexOf(String(active.id))
    const newIndex = rowKeys.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    setRowKeys((prev) => arrayMove(prev, oldIndex, newIndex))
    onChange(arrayMove(items, oldIndex, newIndex))
  }

  const insertAt = (index: number) => {
    if (maxItems !== undefined && items.length >= maxItems) return
    const next = [...items]
    next.splice(index + 1, 0, { text: '' })
    setRowKeys((prev) => {
      const keyNext = [...prev]
      keyNext.splice(index + 1, 0, newRowKey())
      return keyNext
    })
    onChange(next)
  }

  const removeAt = (index: number) => {
    setRowKeys((prev) => prev.filter((_, i) => i !== index))
    onChange(items.filter((_, i) => i !== index))
  }

  const updateAt = (index: number, text: string) => {
    const next = [...items]
    next[index] = { ...next[index], text }
    onChange(next)
  }

  const atMax = maxItems !== undefined && items.length >= maxItems
  const showAddButton = !readOnly && Boolean(addButtonLabel) && items.length === 0

  const appendRow = () => {
    if (atMax) return
    onChange([...items, { text: '' }])
    setRowKeys((prev) => [...prev, newRowKey()])
  }

  const listBody = (
    <div className="space-y-2">
      {items.map((item, index) => {
        const path = `${pathPrefix}.${index}`
        return (
          <SortableCommentaryRow
            key={rowKeys[index] ?? path}
            rowKey={rowKeys[index] ?? path}
            text={item.text}
            readOnly={readOnly}
            unverified={unverifiedPaths.has(path)}
            unverifiedReason={unverifiedMatches.find((m) => m.path === path)?.reason}
            onTextChange={(text) => updateAt(index, text)}
            onRemove={() => removeAt(index)}
            onInsertBelow={() => insertAt(index)}
            insertDisabled={atMax}
          />
        )
      })}
      {showAddButton ? (
        <Button type="button" variant="ghost" size="sm" onClick={appendRow}>
          {addButtonLabel}
        </Button>
      ) : null}
    </div>
  )

  const errorBanner = generationError
    ? (
        <WarningBanner
          className="mb-3"
          message={`Commentary generation failed: ${generationError}. Use Draft to retry.`}
        />
      )
    : null

  if (readOnly) {
    return (
      <>
        {errorBanner}
        {listBody}
      </>
    )
  }

  return (
    <>
      {errorBanner}
      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rowKeys} strategy={verticalListSortingStrategy}>
          {listBody}
        </SortableContext>
      </DndContext>
    </>
  )
}
